import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Loader2, Smartphone, KeyRound, ArrowLeft, Copy, Check } from 'lucide-react';

interface MfaEnrollProps {
  onSuccess: () => void;
  onCancel?: () => void;
  defaultMethod?: 'totp' | 'phone';
}

type Method = null | 'totp' | 'phone';

export function MfaEnroll({ onSuccess, onCancel, defaultMethod }: MfaEnrollProps) {
  const [method, setMethod] = useState<Method>(defaultMethod ?? null);
  const [error, setError] = useState<string | null>(null);

  // TOTP state
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [copiedSecret, setCopiedSecret] = useState(false);

  // Phone state
  const [phone, setPhone] = useState('');

  // Shared state
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [enrolling, setEnrolling] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [step, setStep] = useState<'choose' | 'setup' | 'verify'>(
    defaultMethod ? 'setup' : 'choose'
  );

  // Auto-start TOTP enrollment when defaultMethod is 'totp'
  useEffect(() => {
    if (defaultMethod === 'totp') {
      handleTotpEnroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTotpEnroll = async () => {
    setEnrolling(true);
    setError(null);

    try {
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'QBN Authenticator',
      });

      if (enrollError) throw enrollError;
      if (!data) throw new Error('No enrollment data returned');

      setFactorId(data.id);
      if (data.type === 'totp') {
        setQrCode(data.totp.qr_code);
        setSecret(data.totp.secret);
      }
      setStep('setup');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setEnrolling(false);
    }
  };

  const handlePhoneEnroll = async () => {
    if (!phone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    setEnrolling(true);
    setError(null);

    try {
      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: 'phone',
        phone: phone.trim(),
      });

      if (enrollError) throw enrollError;
      if (!data) throw new Error('No enrollment data returned');

      setFactorId(data.id);

      // Create challenge to send SMS
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: data.id,
      });

      if (challengeError) throw challengeError;
      if (challenge) setChallengeId(challenge.id);

      setStep('verify');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setEnrolling(false);
    }
  };

  const handleTotpStartVerify = async () => {
    if (!factorId) return;
    setError(null);

    try {
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;
      if (challenge) setChallengeId(challenge.id);
      setStep('verify');
    } catch (e: any) {
      setError(e.message);
    }
  };

  const handleVerify = async () => {
    if (!factorId || !challengeId || code.length !== 6) return;

    setVerifying(true);
    setError(null);

    try {
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code,
      });

      if (verifyError) {
        setError(verifyError.message);
        setCode('');
        setVerifying(false);
        return;
      }

      await supabase.auth.refreshSession();
      onSuccess();
    } catch (e: any) {
      setError(e.message);
      setVerifying(false);
    }
  };

  const handleCopySecret = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } catch {
      // Clipboard API not available
    }
  };

  // Step 1: Choose method
  if (step === 'choose') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle>Set Up Two-Factor Authentication</CardTitle>
            <CardDescription>
              Choose how you'd like to receive verification codes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4 px-4"
              onClick={() => {
                setMethod('totp');
                handleTotpEnroll();
              }}
              disabled={enrolling}
            >
              <KeyRound className="h-5 w-5 mr-3 shrink-0" />
              <div className="text-left">
                <div className="font-medium">Authenticator App</div>
                <div className="text-sm text-muted-foreground font-normal">
                  Use Google Authenticator, Authy, or similar
                </div>
              </div>
              {enrolling && method === 'totp' && (
                <Loader2 className="h-4 w-4 ml-auto animate-spin" />
              )}
            </Button>

            <Button
              variant="outline"
              className="w-full justify-start h-auto py-4 px-4"
              onClick={() => {
                setMethod('phone');
                setStep('setup');
              }}
              disabled={enrolling}
            >
              <Smartphone className="h-5 w-5 mr-3 shrink-0" />
              <div className="text-left">
                <div className="font-medium">Text Message (SMS)</div>
                <div className="text-sm text-muted-foreground font-normal">
                  Receive codes via text message
                </div>
              </div>
            </Button>

            {error && (
              <p className="text-sm text-destructive text-center">{error}</p>
            )}

            {onCancel && (
              <Button variant="ghost" onClick={onCancel} className="w-full mt-2">
                Cancel
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 2: Setup (TOTP QR or Phone number)
  if (step === 'setup') {
    if (method === 'totp') {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <Button
                variant="ghost"
                size="sm"
                className="w-fit -ml-2 mb-2"
                onClick={() => {
                  if (defaultMethod && onCancel) { onCancel(); }
                  else { setStep('choose'); setError(null); }
                }}
              >
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <CardTitle>Scan QR Code</CardTitle>
              <CardDescription>
                Scan this QR code with your authenticator app
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {enrolling && !qrCode && (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}

              {qrCode && (
                <div className="flex justify-center p-4 bg-white rounded-lg">
                  <img src={qrCode} alt="MFA QR Code" className="w-48 h-48" />
                </div>
              )}

              {secret && (
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">
                    Or enter this key manually:
                  </p>
                  <div className="flex items-center gap-2 p-2 bg-muted rounded text-sm font-mono">
                    <span className="truncate flex-1">{secret}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="shrink-0"
                      onClick={handleCopySecret}
                    >
                      {copiedSecret ? (
                        <Check className="h-3 w-3" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {error && (
                <p className="text-sm text-destructive text-center">{error}</p>
              )}

              <Button onClick={handleTotpStartVerify} className="w-full" disabled={!qrCode}>
                I've scanned the code
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    // Phone setup
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <Button
              variant="ghost"
              size="sm"
              className="w-fit -ml-2 mb-2"
              onClick={() => {
                if (defaultMethod && onCancel) { onCancel(); }
                else { setStep('choose'); setError(null); }
              }}
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Back
            </Button>
            <CardTitle>Enter Phone Number</CardTitle>
            <CardDescription>
              We'll send you a verification code via SMS
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              type="tel"
              placeholder="+1 555 555 5555"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Enter your number in international format (e.g. +1 for US)
            </p>

            {error && (
              <p className="text-sm text-destructive text-center">{error}</p>
            )}

            <Button
              onClick={handlePhoneEnroll}
              disabled={!phone.trim() || enrolling}
              className="w-full"
            >
              {enrolling ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Send Code
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Step 3: Verify code
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Enter Verification Code</CardTitle>
          <CardDescription>
            {method === 'phone'
              ? 'Enter the 6-digit code sent to your phone'
              : 'Enter the 6-digit code from your authenticator app'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={code}
              onChange={setCode}
              onComplete={handleVerify}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          {error && (
            <p className="text-sm text-destructive text-center">{error}</p>
          )}

          <Button
            onClick={handleVerify}
            disabled={code.length !== 6 || verifying}
            className="w-full"
          >
            {verifying ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Verify & Enable
          </Button>

          <Button
            variant="ghost"
            onClick={() => { setStep('setup'); setCode(''); setError(null); }}
            className="w-full"
          >
            Back
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
